// src/services/llmService.js

import LLM_API_CONFIG from "../config/llmApiConfig";

class LlmService {
  constructor(config = LLM_API_CONFIG) {
    this.config = config;
    this.conversationHistory = [];
    this.systemMessage = null;
  }

  // Prepare headers with API key - no Authorization header for Gemini
  // The API key is sent in the x-goog-api-key header already set in config
  getHeaders() {
    return this.config.headers;
  }

  // Update conversation history
  updateHistory(userMessage, botResponse) {
    this.conversationHistory.push(
      { role: "user", parts: [{ text: userMessage }] },
      { role: "model", parts: [{ text: botResponse }] }
    );

    // Maintain history within limits
    if (this.conversationHistory.length > this.config.maxContextLength * 2) {
      // Keep system instruction if any and trim oldest messages
      const systemInstruction = this.systemMessage ? 1 : 0;
      this.conversationHistory = this.conversationHistory.slice(
        -(this.config.maxContextLength * 2 - systemInstruction)
      );
    }
  }

  // Reset conversation history
  resetConversation() {
    this.conversationHistory = [];
    // Re-add system message if it exists
    if (this.systemMessage) {
      this.conversationHistory.unshift({
        role: "model",
        parts: [{ text: this.systemMessage }],
      });
    }
  }

  // Convert conversation history to Gemini format
  formatConversationForGemini() {
    // For Gemini, first message starts with user
    const formattedHistory = [...this.conversationHistory];

    // Ensure conversation starts with user if there's no system message
    if (
      formattedHistory.length > 0 &&
      formattedHistory[0].role === "model" &&
      !this.systemMessage
    ) {
      formattedHistory.shift();
    }

    return formattedHistory;
  }

  // Main method to send message to LLM
  async sendMessage(userMessage, customParams = {}) {
    try {
      // Prepare the conversation content for Gemini
      const userContent = { role: "user", parts: [{ text: userMessage }] };

      // Format existing conversation history
      const contents = this.formatConversationForGemini();

      // Add current user message
      contents.push(userContent);

      // Prepare request body for Gemini
      const requestBody = {
        ...this.config.defaultParams,
        contents,
      };

      // Apply any custom parameters
      if (customParams.generationConfig) {
        requestBody.generationConfig = {
          ...requestBody.generationConfig,
          ...customParams.generationConfig,
        };
      }

      if (customParams.safetySettings) {
        requestBody.safetySettings = customParams.safetySettings;
      }

      // Set up fetch options
      const fetchOptions = {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody),
      };

      // Add AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.config.timeout
      );
      fetchOptions.signal = controller.signal;

      // Send request to API
      const response = await fetch(this.config.apiEndpoint, fetchOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `API Error (${response.status}): ${
            errorData.error?.message || response.statusText
          }`
        );
      }

      const data = await response.json();

      // Extract response text - Gemini response format is different
      const botResponse =
        data.candidates?.[0]?.content?.parts?.[0]?.text ||
        "Sorry, I could not generate a response.";

      // Update conversation history
      this.updateHistory(userMessage, botResponse);

      return botResponse;
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timeout. Please try again.");
      }

      console.error("LLM API Error:", error);
      throw error;
    }
  }

  // Method for streaming responses (if supported by the API)
  async streamMessage(userMessage, onChunk, customParams = {}) {
    try {
      // Prepare the conversation content for Gemini
      const userContent = { role: "user", parts: [{ text: userMessage }] };

      // Format existing conversation history
      const contents = this.formatConversationForGemini();

      // Add current user message
      contents.push(userContent);

      // Prepare request body for Gemini with streaming enabled
      const requestBody = {
        ...this.config.defaultParams,
        contents,
      };

      // Apply any custom parameters
      if (customParams.generationConfig) {
        requestBody.generationConfig = {
          ...requestBody.generationConfig,
          ...customParams.generationConfig,
        };
      }

      if (customParams.safetySettings) {
        requestBody.safetySettings = customParams.safetySettings;
      }

      // Set up URL for streaming
      const streamUrl = `${this.config.apiEndpoint}?alt=sse`;

      // Set up fetch options
      const fetchOptions = {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody),
      };

      // Add AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.config.timeout
      );
      fetchOptions.signal = controller.signal;

      // Send request to API
      const response = await fetch(streamUrl, fetchOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `API Error (${response.status}): ${
            errorData.error?.message || response.statusText
          }`
        );
      }

      // Process the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let completeResponse = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode the stream chunk
        const chunk = decoder.decode(value, { stream: true });

        // Process the chunk (format for Gemini's SSE format)
        const lines = chunk
          .split("\n")
          .filter((line) => line.trim() !== "" && line.startsWith("data: "));

        for (const line of lines) {
          try {
            const jsonStr = line.replace(/^data: /, "").trim();
            if (!jsonStr || jsonStr === "[DONE]") continue;

            const json = JSON.parse(jsonStr);
            const content =
              json.candidates?.[0]?.content?.parts?.[0]?.text || "";

            if (content) {
              completeResponse += content;
              onChunk(content);
            }
          } catch (e) {
            console.warn("Error parsing stream chunk", e);
          }
        }
      }

      // Update conversation history with complete response
      this.updateHistory(userMessage, completeResponse);

      return completeResponse;
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timeout. Please try again.");
      }

      console.error("LLM API Streaming Error:", error);
      throw error;
    }
  }

  // Add system message to guide the LLM's behavior
  setSystemMessage(content) {
    this.systemMessage = content;

    // Remove any existing system instruction
    this.conversationHistory = this.conversationHistory.filter(
      (msg) => !(msg.role === "model" && msg === this.conversationHistory[0])
    );

    // Add the new system message at the beginning
    // For Gemini, system instructions are typically sent as the first "model" message
    if (content) {
      this.conversationHistory.unshift({
        role: "model",
        parts: [{ text: content }],
      });
    }
  }
}

// Create and export a singleton instance
const llmService = new LlmService();
export default llmService;
